import { Alert, Button, Modal } from '@catering/ui';
import type { JSX } from 'react';
import { useState } from 'react';

import { Order, OrderLineInput } from './api';
import { describeOrderError } from './error-messages';

export interface ChangeRequestFormModalProps {
  order: Order;
  submitting: boolean;
  error: unknown;
  onSubmit: (items: OrderLineInput[]) => void | Promise<void>;
  onClose: () => void;
}

/**
 * `portal-orders/spec.md` → "Solicitud de cambio post-corte creada desde
 * el portal": edits the order's own lines (quantity/notes) — there is no
 * `serviceDate` to fetch an unrelated menu calendar for a `CONFIRMED`
 * order, so this only lets the user adjust what is already on the order,
 * matching the spec's own scenario language ("solicita aumentar una
 * línea"), not add an entirely new menu.
 */
export function ChangeRequestFormModal({
  order,
  submitting,
  error,
  onSubmit,
  onClose,
}: ChangeRequestFormModalProps): JSX.Element {
  // Legacy items with no `positionId` (`design.md` §1.4, T6) cannot be
  // requested by position, same choice `order-form.tsx` makes.
  const positionedItems = order.items.filter(
    (item): item is typeof item & { positionId: string } => item.positionId !== null,
  );
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(positionedItems.map((item) => [item.positionId, item.quantity])),
  );
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(positionedItems.map((item) => [item.positionId, item.notes ?? ''])),
  );

  const errorMessage = describeOrderError(error);

  // The API replaces the order's lines with the requested list, so the request
  // carries every dish that stays; a dish set to 0 is left out and gets removed.
  const items: OrderLineInput[] = positionedItems
    .map((item) => ({
      positionId: item.positionId,
      quantity: quantities[item.positionId] ?? item.quantity,
      notes: notes[item.positionId] || null,
    }))
    .filter((item) => Number.isInteger(item.quantity) && item.quantity > 0);
  const changed = positionedItems.some(
    (item) =>
      (quantities[item.positionId] ?? item.quantity) !== item.quantity ||
      (notes[item.positionId] ?? '') !== (item.notes ?? ''),
  );
  const blockedReason = !changed
    ? 'No cambiaste ninguna cantidad.'
    : items.length === 0
      ? 'Dejá al menos un plato. Para cancelar todo el pedido, hablá con tu proveedor.'
      : null;

  function handleSubmit(): void {
    if (blockedReason === null) {
      void onSubmit(items);
    }
  }

  return (
    <Modal open onClose={onClose} title="Solicitar cambio" className="max-w-2xl">
      <div className="flex flex-col gap-4">
        {error !== undefined && error !== null ? <Alert tone="danger">{errorMessage}</Alert> : null}

        <p className="text-sm text-slate-700">
          Un usuario de Operaciones va a aprobar o rechazar tu solicitud. Para quitar un plato,
          ponele 0 en la cantidad.
        </p>

        <div className="flex flex-col gap-3">
          {positionedItems.map((item) => (
            <div
              key={item.positionId}
              className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3"
            >
              <div className="flex flex-col">
                {item.position !== null ? (
                  <span className="text-xs uppercase tracking-wide text-slate-500">
                    {item.position.name}
                  </span>
                ) : null}
                <span className="text-sm font-medium text-slate-900">{item.menuNameSnapshot}</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  aria-label={`Observaciones de ${item.menuNameSnapshot}`}
                  placeholder="Observaciones"
                  value={notes[item.positionId] ?? ''}
                  onChange={(event) =>
                    setNotes((current) => ({ ...current, [item.positionId]: event.target.value }))
                  }
                  className="h-11 w-32 rounded-md border border-slate-300 px-2 text-sm"
                />
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  aria-label={`Cantidad de ${item.menuNameSnapshot}`}
                  value={quantities[item.positionId] ?? 0}
                  onChange={(event) =>
                    setQuantities((current) => ({
                      ...current,
                      [item.positionId]: Number(event.target.value),
                    }))
                  }
                  onFocus={(event) => event.target.select()}
                  className="h-11 w-20 rounded-md border border-slate-300 px-2 text-center text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        {blockedReason !== null ? <p className="text-sm text-slate-600">{blockedReason}</p> : null}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={submitting} disabled={blockedReason !== null} onClick={handleSubmit}>
            Enviar solicitud
          </Button>
        </div>
      </div>
    </Modal>
  );
}
